import { Dispatch, FC, SetStateAction, useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { NextData } from 'components/features/accountcreation/NewAccountMain'
import NewAccountVerifyYourPhoneNumberForm from 'components/organisms/NewAccountVerifyYourPhoneNumberForm'
import {
  AccountCreationError,
  getCorrectError
} from 'share/utils/codeVerificationUtils'
import { AuthVerifyContainer } from 'components/atoms/Layout'
import {
  START_PHONE_VERIFICATION,
  COMPLETE_PHONE_VERIFICATION,
  StartPhoneVerificationResponse,
  CompletePhoneVerificationResponse,
  CompletePhoneVerificationVariables
} from 'share/mutations/accountcreation'

interface NewAccountVerifyYourPhoneNumberProps {
  codeValue: string
  invitationId: string
  nextStep(data: NextData): void
  resendVerificationCode(): void
  setCode: Dispatch<SetStateAction<string>>
  setError(error: AccountCreationError): void
  phoneNumber?: string | null
  verifyPhone: boolean
  verifyEmail: boolean
}

const NewAccountVerifyYourPhoneNumber: FC<NewAccountVerifyYourPhoneNumberProps> =
  ({
    codeValue,
    nextStep,
    phoneNumber,
    resendVerificationCode,
    setCode,
    setError,
    verifyPhone,
    verifyEmail
  }) => {
    const [enteredCodeComplete, setEnteredCodeComplete] = useState(false)

    const [startPhoneVerification] =
      useMutation<StartPhoneVerificationResponse>(START_PHONE_VERIFICATION)

    const [completePhoneVerification, { loading }] = useMutation<
      CompletePhoneVerificationResponse,
      CompletePhoneVerificationVariables
    >(COMPLETE_PHONE_VERIFICATION)

    useEffect(() => {
      startPhoneVerificationHelper()
    }, [])

    const loadingDelay = useLoadingDelay(loading)

    const lastFour =
      phoneNumber && phoneNumber.length > 4
        ? phoneNumber.substring(phoneNumber.length - 4)
        : ''

    const startPhoneVerificationHelper = () => {
      // call api to send email verification code
      startPhoneVerification()
        .then((result) => {
          console.log('startPhoneVerification result is', result)
        })
        .catch((e: Error) => {
          setError(AccountCreationError.GeneralError)
          console.log('startPhoneVerification failed error is', e)
        })
    }

    const completePhoneVerificationHelper = async (input: string) => {
      try {
        const verificationResult = await completePhoneVerification({
          variables: { code: input }
        })

        // Valid verification code
        if (
          verificationResult.data?.authentication.submitPhoneVerificationCode
            .success
        ) {
          setError(AccountCreationError.NoError)
          nextStep({ verifyPhone, verifyEmail })
        }
        // Invalid or expired verification code
        else {
          const responseError =
            verificationResult.data?.authentication.submitPhoneVerificationCode
              .error
          const correctError = getCorrectError(responseError)
          setError(correctError)
        }
      } catch (err) {
        setError(AccountCreationError.GeneralError)
      }
    }

    return (
      <div className='default-size-container'>
        <AuthVerifyContainer
          data-testid='create-account-account-verify-phone-page'
          className='pl-0 pr-0'
        >
          {loadingDelay && loadingSpinnerPortal()}
          <h1 data-testid='create-account-account-verify-phone-header'>
            Verify your phone number
          </h1>
          <span
            className='verify-message-text'
            data-testid='create-account-account-verify-phone-message'
          >
            A verification code has been sent to your mobile number,
            <strong data-testid='create-account-account-verify-phone-last-four'>
              {' '}
              ***-***-{lastFour}.
            </strong>
          </span>
          <NewAccountVerifyYourPhoneNumberForm
            completePhoneVerificationHelper={completePhoneVerificationHelper}
            enteredCode={codeValue}
            enteredCodeComplete={enteredCodeComplete}
            resendVerificationCode={resendVerificationCode}
            setEnteredCode={setCode}
            setEnteredCodeComplete={setEnteredCodeComplete}
          />
        </AuthVerifyContainer>
      </div>
    )
  }

export default NewAccountVerifyYourPhoneNumber
